import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, can } from '../../lib/api';
import type { Page, Site } from '../../lib/types';
import {
  Badge, Banner, Button, Card, Empty, Field, LoadError, Segmented, Skeleton, input,
} from '../../lib/ui';
import { categoryLabel, modeLabel, reasonLabel, regimeLabel, regimeLong, sourceLabel } from '../../lib/labels';
import { useConfirm } from '../../lib/Modal';
import { DeclarationCard } from './DeclarationCard';
import { DomainsCard } from './DomainsCard';
import { Hilfe } from '../help/Help';
import { t, tn } from '../../lib/i18n';

/**
 * What gets stopped before consent, and who is disclosed for it (Doc 02 §5).
 *
 * The screen leads with the effective set rather than with the site's own rules, because
 * "what does my site actually block" is the question people come here with. Their own
 * rules are one contribution to that answer, not the answer.
 */
interface EffectiveRule {
  match: string;
  value: string;
  category?: string;
  label?: string;
  cta?: string;
}
interface Effective {
  regime: {
    resolved: string;
    mode: string;
    require_reject_button: boolean;
    block_before_consent: boolean;
    consent_lifetime_days: number;
  };
  reason: string;
  rules: {
    block?: EffectiveRule[];
    allow?: EffectiveRule[];
    cookies?: EffectiveRule[];
    placeholders?: EffectiveRule[];
  };
}
interface Vendor {
  id: string;
  vendor_key: string;
  name: string;
  category_slug: string;
  policy_url: string;
  retention: string;
  cookies: string[];
  domains: string[];
  source: string;
}
interface Rule {
  id: string;
  kind: string;
  match_type: string;
  match_value: string;
  category_slug: string;
}

const CATEGORIES = ['necessary', 'functional', 'analytics', 'marketing'];

/**
 * The rule builder's own vocabulary.
 *
 * Its four dropdowns used to offer the wire values — "block", "domain", "content" — which
 * is a fine API and a poor question. "regex" and "content" in particular are impossible to
 * choose between without knowing the implementation.
 */
// German values are the translation keys; each is passed through t() at its render site,
// because a t() out here would freeze the language at import.
const KIND: Record<string, string> = {
  block: 'Blockieren',
  allow: 'Erlauben',
  cookie: 'Cookie',
  placeholder: 'Klick-zum-Laden',
};
const MATCH: Record<string, string> = {
  domain: 'Domain',
  regex: 'Regulärer Ausdruck',
  content: 'Im Seitentext',
  prefix: 'Beginnt mit',
  exact: 'Genau',
};

export function BlockingScreen({ site }: { site: Site }) {
  const qc = useQueryClient();
  const base = `/v1/sites/${site.id}`;
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['effective', site.id] });
    void qc.invalidateQueries({ queryKey: ['rules', site.id] });
    void qc.invalidateQueries({ queryKey: ['vendors', site.id] });
  };

  const effective = useQuery({
    queryKey: ['effective', site.id],
    queryFn: () => api<Effective>(`${base}/rules/effective`),
  });
  const own = useQuery({
    queryKey: ['rules', site.id],
    queryFn: () => api<Page<Rule>>(`${base}/rules`),
  });
  const vendors = useQuery({
    queryKey: ['vendors', site.id],
    queryFn: () => api<Page<Vendor>>(`${base}/vendors`),
  });
  // Offene Vorschläge liegen seit der Trennung von Scan und Blockieren in einem anderen
  // Tab. Ohne diesen Zähler sagt die Anbieter-Karte "noch keine gepflegt", während nebenan
  // vier auf eine Entscheidung warten — und ein nicht entschiedener Anbieter fehlt im
  // Auswahl-Dialog als Empfänger.
  const suggestions = useQuery({
    queryKey: ['suggestions', site.id],
    queryFn: () => api<{ data: unknown[] }>(`${base}/scan/suggestions`),
  });

  const ownIds = new Set((own.data?.data ?? []).map((r) => `${r.match_type}\u0000${r.match_value}`));
  const eff = effective.data;
  const [confirmNode, confirm] = useConfirm();

  const rules = eff?.rules ?? {};
  const all: Row[] = [
    ...(rules.block ?? []).map((r) => ({ ...r, kind: 'block' as const })),
    ...(rules.cookies ?? []).map((r) => ({ ...r, kind: 'cookie' as const })),
    ...(rules.placeholders ?? []).map((r) => ({ ...r, kind: 'placeholder' as const })),
    ...(rules.allow ?? []).map((r) => ({ ...r, kind: 'allow' as const })),
  ];

  return (
    <div className="space-y-4">
      {confirmNode}

      {/* An error here used to render as "(0) — Nichts.", i.e. the console telling a
          compliance user that nothing is blocked when it simply could not find out. On
          this screen that is the most dangerous sentence in the product. */}
      {effective.isError && (
        <LoadError what={t('Die geltenden Regeln')} onRetry={() => void effective.refetch()} />
      )}

      <StatusBar eff={eff} loading={effective.isPending} count={all.length} site={site} />

      <RuleTable
        rows={all}
        ownIds={ownIds}
        own={own.data?.data ?? []}
        loading={effective.isPending}
        failed={effective.isError}
        base={base}
        siteId={site.id}
        onChanged={invalidate}
        confirm={confirm}
      />

      <Vendors vendors={vendors.data?.data ?? []} base={base} onChanged={invalidate} confirm={confirm}
        siteId={site.id} pending={suggestions.data?.data.length ?? 0} />

      <DeclarationCard site={site} />

      <DomainsCard siteId={site.id} />
    </div>
  );
}

function Fact({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div title={title}>
      <div className="eyebrow">{label}</div>
      <div className="mt-0.5 text-[13px] font-medium">{value}</div>
    </div>
  );
}

type Kind = 'block' | 'cookie' | 'placeholder' | 'allow';
type Row = EffectiveRule & { kind: Kind };

const KIND_LABEL: Record<Kind, string> = {
  block: 'Blockieren',
  cookie: 'Cookie',
  placeholder: 'Klick-zum-Laden',
  allow: 'Ausnahme',
};
const kindLabel = (k: Kind): string => t(KIND_LABEL[k]);

/**
 * The answer, before the inventory.
 *
 * The screen used to open with a card of five facts and then four lists of domains. What
 * someone actually arrives wanting to know is "is my site currently letting anything
 * through", and that was buried three cards down inside the scan panel.
 */
function StatusBar({ eff, loading, count, site }: {
  eff: Effective | undefined; loading: boolean; count: number; site: Site;
}) {
  const r = eff?.regime;
  return (
    <Card title={t('Rechtslage')} actions={
      <span className="flex items-center gap-2">
        {eff && <span className="text-xs text-[var(--color-muted)]">{reasonLabel(eff.reason)}</span>}
        <Hilfe to="rechtslage" />
      </span>
    }>
      {loading ? <Skeleton rows={1} /> : !r ? null : (
        <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
          <Fact label={t('Regime')} value={regimeLabel(r.resolved)} title={regimeLong(r.resolved)} />
          <Fact label={t('Grundlage')} value={modeLabel(r.mode)} />
          <Fact label={t('Ablehnen-Button')}
            value={r.require_reject_button ? t('Verpflichtend') : t('Optional')} />
          <Fact label={t('Einwilligung gilt')}
            value={t('{n} Tage', { n: r.consent_lifetime_days })} />
          <Fact label={t('Regeln aktiv')} value={String(count)} />
          <Fact label={t('Website')} value={site.primary_domain} />
        </div>
      )}
    </Card>
  );
}

/**
 * One table instead of four lists.
 *
 * The four kinds were four cards of identical monospace domains — 43 of them here — that
 * nobody reads top to bottom. The question people actually arrive with is about one
 * vendor: "why is doubleclick blocked", "is our own tracker in there". That is a lookup,
 * so this is searchable, and the kind becomes a filter rather than a heading.
 *
 * The site's own rules live in the same table, marked and removable there. They were a
 * separate card listing the same rules a second time, which meant a rule someone had just
 * added appeared twice on one screen in two different notations.
 */
function RuleTable({ rows, ownIds, own, loading, failed, base, siteId, onChanged, confirm }: {
  rows: Row[];
  ownIds: Set<string>;
  own: Rule[];
  loading: boolean;
  failed: boolean;
  base: string;
  siteId: string;
  onChanged: () => void;
  confirm: Confirm;
}) {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<Kind | 'all'>('all');
  const [adding, setAdding] = useState(false);

  const byId = new Map(own.map((r) => [`${r.match_type} ${r.match_value}`, r]));
  const counts = rows.reduce<Record<string, number>>((a, r) => {
    a[r.kind] = (a[r.kind] ?? 0) + 1;
    return a;
  }, {});

  const needle = q.trim().toLowerCase();
  const shown = rows.filter((r) =>
    (kind === 'all' || r.kind === kind)
    && (needle === '' || r.value.toLowerCase().includes(needle)
      || (r.category ?? '').toLowerCase().includes(needle)
      || categoryLabel(r.category).toLowerCase().includes(needle)));

  const remove = useMutation({
    mutationFn: (id: string) => api(`${base}/rules/${id}`, { method: 'DELETE' }),
    onSuccess: onChanged,
  });
  const writable = can('config:write');

  return (
    <Card
      title={t('Regeln')}
      count={failed || loading ? undefined : rows.length}
      actions={
        <span className="flex items-center gap-1">
          {writable && (
            <Button variant="secondary" size="sm" onClick={() => setAdding(!adding)}>
              {adding ? t('Abbrechen') : t('Regel hinzufügen')}
            </Button>
          )}
          <Hilfe to="regeln" />
        </span>
      }
    >
      {adding && writable && (
        <div className="mb-4">
          <AddRule base={base} onChanged={() => { onChanged(); setAdding(false); }} />
        </div>
      )}

      {failed ? (
        <p className="text-sm text-[var(--color-danger)]">
          {t('Unbekannt — die geltenden Regeln konnten nicht geladen werden.')}
        </p>
      ) : loading ? (
        <Skeleton rows={6} />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <input
              className={`${input} max-w-[280px]`}
              value={q}
              placeholder={t('Suchen — z. B. doubleclick')}
              aria-label={t('Regeln durchsuchen')}
              onChange={(e) => setQ(e.target.value)}
            />
            <Segmented
              size="sm"
              value={kind}
              onChange={setKind}
              options={[
                { value: 'all', label: t('Alle {n}', { n: rows.length }) },
                ...(Object.keys(KIND_LABEL) as Kind[])
                  .filter((k) => counts[k])
                  .map((k) => ({ value: k, label: `${kindLabel(k)} ${counts[k]}` })),
              ]}
            />
          </div>

          {shown.length === 0 ? (
            <p className="py-6 text-center text-sm text-[var(--color-muted)]">
              {needle ? t('Keine Regel enthält „{needle}“.', { needle: q }) : t('Nichts.')}
            </p>
          ) : (
            // Scrolls rather than growing: 43 rules in one card made the page longer than
            // the four separate lists it replaced, and a search box that scrolls out of
            // view while you read the results is not a search box.
            <ul className="max-h-[380px] divide-y divide-[var(--color-line)] overflow-y-auto
                           rounded-[3px] border border-[var(--color-line)] px-3">
              {shown.map((r) => {
                const mine = byId.get(`${r.match} ${r.value}`);
                return (
                  <li key={`${r.kind}:${r.match}:${r.value}`}
                    className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-1.5">
                    <span className="flex min-w-0 items-baseline gap-2">
                      <span className="truncate font-mono text-[13px]" title={r.value}>
                        <Mark text={r.value} needle={needle} />
                      </span>
                      <Badge tone={r.kind === 'allow' ? 'live' : 'neutral'}>{kindLabel(r.kind)}</Badge>
                    </span>
                    <span className="flex shrink-0 items-center gap-3 text-xs text-[var(--color-muted)]">
                      {r.category && <span>{categoryLabel(r.category)}</span>}
                      {/* Where a rule comes from decides whether it can be removed here,
                          so it is on the row rather than in a legend. */}
                      {ownIds.has(`${r.match} ${r.value}`)
                        ? <span className="text-[var(--color-brand)]">{t('eigene')}</span>
                        : <span>{t('Plattform')}</span>}
                      {mine && writable && (
                        <button className="text-[var(--color-danger)] hover:underline"
                          onClick={() => confirm({
                            title: t('Regel entfernen?'),
                            danger: true,
                            confirmLabel: t('Entfernen'),
                            body: (
                              <>
                                {r.kind === 'allow'
                                  ? t('{value} wird ab der nächsten Veröffentlichung nicht mehr ausgenommen.', { value: r.value })
                                  : t('{value} wird ab der nächsten Veröffentlichung nicht mehr angehalten.', { value: r.value })}
                              </>
                            ),
                            onConfirm: () => remove.mutate(mine.id),
                          })}>
                          {t('Entfernen')}
                        </button>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {/* The sentence has always been true and always ended nowhere: the control it
              names lives on another tab. A rule saved here and never published is the
              most expensive way this screen can be wrong, so it gets the way out. */}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="min-w-[16rem] flex-1 text-xs text-[var(--color-muted)]">
              {t('Plattform-Regeln gelten immer. Eigene kommen additiv dazu — erlauben gewinnt gegen blockieren, und Änderungen wirken erst nach dem nächsten Veröffentlichen.')}
            </p>
            <Button variant="secondary" size="sm" href={`#/sites/${siteId}/banner`}
              title={t('Regeln werden im Banner-Editor veröffentlicht — dort steht der Knopf für die ganze Website.')}>
              {t('Zum Veröffentlichen')}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

/** Highlights the search term, so a hit in a 40-character domain is findable by eye. */
function Mark({ text, needle }: { text: string; needle: string }) {
  if (!needle) return <>{text}</>;
  const at = text.toLowerCase().indexOf(needle);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="bg-[var(--color-note)] text-[var(--color-note-ink)]">
        {text.slice(at, at + needle.length)}
      </mark>
      {text.slice(at + needle.length)}
    </>
  );
}

type Confirm = ReturnType<typeof useConfirm>[1];

function AddRule({ base, onChanged }: { base: string; onChanged: () => void }) {
  const [kind, setKind] = useState('block');
  const [matchType, setMatchType] = useState('domain');
  const [value, setValue] = useState('');
  const [category, setCategory] = useState('analytics');
  const [problem, setProblem] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      api<Rule>(`${base}/rules`, {
        method: 'POST',
        body: { kind, match_type: matchType, match_value: value, category_slug: kind === 'allow' ? '' : category },
      }),
    onSuccess: () => { setValue(''); setProblem(null); onChanged(); },
    onError: (e) => setProblem(e instanceof ApiError ? ruleProblem(e) : t('Fehlgeschlagen.')),
  });

  return (
    <div className="space-y-3 rounded-[3px] border border-[var(--color-line)] bg-[var(--color-paper)] p-3">
      {problem && <Banner kind="error">{problem}</Banner>}
      <div className="grid gap-2 sm:grid-cols-[130px_150px_1fr_140px_auto]">
        <select className={input} value={kind} onChange={(e) => setKind(e.target.value)} aria-label={t('Art')}>
          {Object.entries(KIND).map(([k, label]) => <option key={k} value={k}>{t(label)}</option>)}
        </select>
        <select className={input} value={matchType} onChange={(e) => setMatchType(e.target.value)} aria-label={t('Treffer')}>
          {(kind === 'cookie' ? ['prefix', 'exact'] : ['domain', 'regex', 'content']).map((m) => (
            <option key={m} value={m}>{t(MATCH[m]!)}</option>
          ))}
        </select>
        <input className={input} value={value} placeholder="beispiel-tracker.de"
          onChange={(e) => setValue(e.target.value)} aria-label={t('Wert')} />
        <select className={input} value={category} disabled={kind === 'allow'}
          onChange={(e) => setCategory(e.target.value)} aria-label={t('Kategorie')}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
        </select>
        <Button disabled={!value.trim() || add.isPending} onClick={() => add.mutate()}>
          {t('Hinzufügen')}
        </Button>
      </div>
    </div>
  );
}

/** Maps the API's field codes to something a human can act on. */
function ruleProblem(e: ApiError): string {
  const codes = (e.problem.errors ?? []).map((x) => x.code);
  if (codes.includes('catastrophic_backtracking')) {
    return t('Dieser reguläre Ausdruck kann den Browser des Besuchers blockieren (verschachtelte Quantoren).');
  }
  if (codes.includes('unbalanced_parentheses')) return t('Klammern im Ausdruck sind unausgeglichen.');
  if (codes.includes('too_long')) return t('Der Ausdruck ist zu lang (max. 200 Zeichen).');
  if (codes.some((c) => c === 'guardrail')) {
    return t('Diese Änderung würde eine DSGVO-Garantie abschwächen und wird nicht gespeichert.');
  }
  return t('Regel ist nicht gültig.');
}

function Vendors({ vendors, base, onChanged, confirm, siteId, pending }: {
  vendors: Vendor[]; base: string; onChanged: () => void; confirm: Confirm;
  siteId: string; pending: number;
}) {
  const [draft, setDraft] = useState<Partial<Vendor> | null>(null);
  const writable = can('vendor:write');

  const save = useMutation({
    mutationFn: (v: Partial<Vendor>) =>
      api<Vendor>(v.id ? `${base}/vendors/${v.id}` : `${base}/vendors`, {
        method: v.id ? 'PATCH' : 'POST',
        body: {
          vendor_key: v.vendor_key ?? '',
          name: v.name ?? '',
          category_slug: v.category_slug ?? 'analytics',
          policy_url: v.policy_url ?? '',
          retention: v.retention ?? '',
          cookies: v.cookies ?? [],
          domains: v.domains ?? [],
        },
      }),
    onSuccess: () => { setDraft(null); onChanged(); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`${base}/vendors/${id}`, { method: 'DELETE' }),
    onSuccess: onChanged,
  });

  return (
    <Card
      title={t('Anbieter')}
      count={vendors.length}
      actions={
        <span className="flex items-center gap-1">
          {writable && !draft && (
            <Button variant="secondary" size="sm" onClick={() => setDraft({ category_slug: 'analytics' })}>
              {t('Anbieter hinzufügen')}
            </Button>
          )}
          <Hilfe to="anbieter" />
        </span>
      }
    >
      <p className="mb-3 text-xs text-[var(--color-muted)]">
        {t('Art. 13 verlangt, dass die Empfänger benannt werden. Diese Liste erscheint im Auswahl-Dialog, sobald sie dort eingeschaltet ist.')}
      </p>

      {pending > 0 && (
        <div className="mb-3">
          <Banner kind="info">
            <span>
              {tn('Aus dem Scan wartet {n} Anbieter auf eine Entscheidung.',
                  'Aus dem Scan warten {n} Anbieter auf eine Entscheidung.', pending)}{' '}
              <a className="underline underline-offset-2" href={`#/sites/${siteId}/scan`}>
                {t('Zum Scan')}
              </a>
            </span>
          </Banner>
        </div>
      )}

      {vendors.length === 0 && !draft && (
        <p className="text-sm text-[var(--color-muted)]">
          {pending > 0
            ? t('Noch keiner übernommen — entschieden wird im Scan.')
            : t('Noch keine Anbieter gepflegt.')}
        </p>
      )}

      <ul className="divide-y divide-[var(--color-line)]">
        {vendors.map((v) => (
          <li key={v.id} className="flex items-start justify-between gap-3 py-2.5">
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-[13px] font-medium">{v.name}</span>
                <Badge>{categoryLabel(v.category_slug)}</Badge>
                <span className="text-xs text-[var(--color-muted)]">{sourceLabel(v.source)}</span>
              </div>
              <div className="mt-0.5 truncate text-xs text-[var(--color-muted)]">
                {[v.retention, v.cookies.join(', ')].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
            {writable && (
              <div className="flex shrink-0 gap-3 text-xs">
                <button className="hover:underline" onClick={() => setDraft(v)}>{t('Bearbeiten')}</button>
                <button className="text-[var(--color-danger)] hover:underline"
                  onClick={() => confirm({
                    title: t('Anbieter entfernen?'),
                    danger: true,
                    confirmLabel: t('Entfernen'),
                    body: <>{t('{name} verschwindet aus dem Auswahl-Dialog. Wenn er weiterhin geladen wird, fehlt er dort als Empfänger.', { name: v.name })}</>,
                    onConfirm: () => remove.mutate(v.id),
                  })}>{t('Entfernen')}</button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {draft && (
        <div className="mt-4 space-y-3 border-t border-[var(--color-line)] pt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('Name')}>
              <input className={input} value={draft.name ?? ''} placeholder="Google Analytics 4"
                onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Field>
            <Field label={t('Schlüssel')} hint={t('Bleibt stabil, auch wenn der Name sich ändert.')}>
              <input className={input} value={draft.vendor_key ?? ''} placeholder="ga4"
                onChange={(e) => setDraft({ ...draft, vendor_key: e.target.value })} />
            </Field>
            <Field label={t('Kategorie')}>
              <select className={input} value={draft.category_slug ?? 'analytics'}
                onChange={(e) => setDraft({ ...draft, category_slug: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
              </select>
            </Field>
            <Field label={t('Speicherdauer')}>
              <input className={input} value={draft.retention ?? ''} placeholder={t('14 Monate')}
                onChange={(e) => setDraft({ ...draft, retention: e.target.value })} />
            </Field>
            <Field label={t('Datenschutzerklärung')}>
              <input className={input} value={draft.policy_url ?? ''} placeholder="https://…"
                onChange={(e) => setDraft({ ...draft, policy_url: e.target.value })} />
            </Field>
            <Field label={t('Cookies')} hint={t('Komma-getrennt.')}>
              <input className={input} value={(draft.cookies ?? []).join(', ')} placeholder="_ga, _ga_*"
                onChange={(e) => setDraft({ ...draft, cookies: splitList(e.target.value) })} />
            </Field>
          </div>
          {save.error && <Banner kind="error">{t('Speichern fehlgeschlagen. Schlüssel schon vergeben?')}</Banner>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDraft(null)}>{t('Abbrechen')}</Button>
            <Button disabled={save.isPending} onClick={() => save.mutate(draft)}>{t('Speichern')}</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

const splitList = (s: string): string[] =>
  s.split(',').map((x) => x.trim()).filter(Boolean);
